import Stack from "@mui/material/Stack";
import { useCallback } from "react";
import { useAnswer } from "src/context/AnswerProvider";
import { useQuestions } from "src/context/QuestionsProvider";
import ChoiceOption from "../ChoiceOption";
import { QuestionOption } from "src/api/types";
import { useContinue } from "src/hooks/useContinue";
import { useVisit } from "src/context/VisitProvider";
import { Paths } from "src/constants/paths";
import { useSubmitAnswerMutation } from "src/api/services/answersApi";
import { useParams } from "react-router-dom";
import { MatchParam } from "src/constants/matches";

const MultipleChoice = () => {
  const options = useQuestions((q) => q.options ?? []);
  const { setAnswers, details } = useAnswer();
  const navigate = useContinue();
  const slug = useQuestions((q) => q.slug);
  const visitType = useVisit((v) => v.visit_type);
  const questionType = useQuestions((q) => q.question_type);
  const next = useVisit(
    (v) => v.questions?.find((q) => q.slug === slug)?.next_question,
  );
  const path =
    visitType === "Refill"
      ? Paths.REFILL
      : visitType === "Upsell"
        ? Paths.ADD_ONS
        : Paths.QUESTIONS;
  const [submitAnswer] = useSubmitAnswerMutation();
  const { questionId, visitId } =
    useParams<MatchParam<"QUESTION_ID" | "VISIT_ID">>();

  const handleSelect = useCallback(
    async (option: QuestionOption) => {
      if (option.value.includes("None")) {
        const newAnswer = [option.value];
        setAnswers(newAnswer);

        if (visitId && questionId) {
          submitAnswer({
            visitId,
            question_id: questionId,
            question_type: questionType,
            response: {
              answer: newAnswer,
              details,
            },
          }).unwrap();
        }
        navigate(`${path}/${next}`);
        return;
      }

      setAnswers((answers) => {
        const noNoneAnswers = answers.filter((a) => !a.includes("None"));

        if (noNoneAnswers.includes(option.value)) {
          return noNoneAnswers.filter((current) => current !== option.value);
        }

        return noNoneAnswers.concat(option.value);
      });
    },

    [
      details,
      navigate,
      next,
      path,
      questionId,
      questionType,
      setAnswers,
      submitAnswer,
      visitId,
    ],
  );

  return (
    <Stack gap="8px">
      {options.map((option) => (
        <ChoiceOption
          option={option}
          key={option.value}
          onSelect={handleSelect}
          isMultipleChoice
        />
      ))}
    </Stack>
  );
};

export default MultipleChoice;
